/**
 * Pending input requests: the shared inbox data layer (prompt.md §23, §78).
 *
 * WHY one hook owns the list, the answers and the notification permission: a
 * question that arrived while another tab was open, a question answered on a
 * phone, and a question whose form the agent revised are the same request seen
 * three ways. Keeping them behind one query and one set of mutations means the
 * web client and the desktop client close the same popup for the same reason.
 *
 * The list is the server's durable pending set. Nothing here is answered without
 * an explicit submit, and every answer is written through the ordinary
 * clarification endpoint so the run resumes exactly once, server-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INBOX_DELIVERY_LIMIT,
  buildInboxViews,
  nextPopupRequest,
  notificationPermissionStatus,
  type FormFieldProblem,
  type FormSubmission,
  type InboxRequestView,
  type NotificationApiLike,
  type NotificationPermissionStatus,
} from "@dayvise/react-ui";

import { queryKeys, type QueryScope } from "./queryKeys.ts";
import { useActiveWorkspaceId, useDayviseClient } from "./providers.tsx";

/**
 * How often an open client reconciles its inbox.
 *
 * A run's stream is per-run, so a client watching conversation A cannot learn from
 * it that conversation B's question was answered on another device. A bounded poll
 * of the durable list is what makes the other device's answer visible, and it is
 * suspended entirely while the tab is hidden.
 */
export const INPUT_REQUEST_POLL_MS = 20_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Polling only while there is a visible surface that could show the result. */
export function pageIsVisible(): boolean {
  const document = (globalThis as { document?: { visibilityState?: string } }).document;
  return document === undefined || document.visibilityState !== "hidden";
}

export function inputRequestPollIntervalMs(): number | false {
  return pageIsVisible() ? INPUT_REQUEST_POLL_MS : false;
}

/** Per-field messages the server returned for a rejected answer; never a value. */
export function answerFieldProblems(error: unknown): readonly FormFieldProblem[] {
  const body = isRecord(error) && isRecord(error.body) ? error.body : null;
  const fields = body !== null && Array.isArray(body.fields) ? body.fields : [];
  return fields.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const fieldId = typeof entry.fieldId === "string" ? entry.fieldId : null;
    const message = typeof entry.message === "string" ? entry.message : null;
    return fieldId === null || message === null ? [] : [{ fieldId, message }];
  });
}

function errorCode(error: unknown): string {
  return isRecord(error) && typeof error.code === "string" ? error.code : "";
}

/** The form changed after this client rendered it: re-read it before answering. */
export function answerIsStale(error: unknown): boolean {
  const code = errorCode(error);
  return code === "stale_revision" || code === "stale_form";
}

/** The request was answered, declined, expired or cancelled somewhere else. */
export function answerSettledElsewhere(error: unknown): boolean {
  const code = errorCode(error);
  return code === "clarification_expired" || code === "conflict" || code === "not_found";
}

export function answerErrorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (answerSettledElsewhere(error)) return "This question was answered, declined or expired elsewhere, so nothing was submitted. The inbox has been refreshed.";
  if (answerIsStale(error)) return "The agent revised this question after it was shown. Reload it before answering.";
  return error instanceof Error ? error.message : "The answer could not be submitted.";
}

export function useInputRequests() {
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    queryKey: queryKeys.inputRequests({ workspaceId }),
    queryFn: () => client.inputRequests.list(),
    enabled: workspaceId !== null,
    refetchInterval: () => inputRequestPollIntervalMs(),
  });
}

export interface InputRequestAnswerControls {
  readonly busy: boolean;
  readonly error: string | null;
  readonly fieldErrors: readonly FormFieldProblem[];
  readonly stale: boolean;
  readonly submitAnswers: (view: InboxRequestView, answers: Readonly<Record<string, unknown>>) => Promise<void>;
  readonly submitForm: (view: InboxRequestView, submission: FormSubmission) => Promise<void>;
  readonly decline: (view: InboxRequestView) => Promise<void>;
}

/** The three ordinary ways to settle a request, each bound to its listed run. */
export function useInputRequestAnswers(): InputRequestAnswerControls {
  const client = useDayviseClient();
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId();

  const refresh = useCallback(() => {
    const scope: QueryScope = { workspaceId };
    void queryClient.invalidateQueries({ queryKey: queryKeys.inputRequests(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.runs(scope) });
  }, [queryClient, workspaceId]);

  // A conflict means the durable row is no longer pending, so the list this client
  // is showing is already wrong: re-read it rather than leaving a dead prompt up.
  const onError = useCallback((error: unknown) => {
    if (answerSettledElsewhere(error)) refresh();
  }, [refresh]);

  const answers = useMutation({
    mutationFn: ({ view, answers }: { readonly view: InboxRequestView; readonly answers: Readonly<Record<string, unknown>> }) =>
      client.inputRequests.answer(view.runId, view.requestId, answers),
    onSuccess: refresh,
    onError,
  });
  const forms = useMutation({
    mutationFn: ({ view, submission }: { readonly view: InboxRequestView; readonly submission: FormSubmission }) =>
      client.inputRequests.answerForm(view.runId, view.requestId, submission),
    onSuccess: refresh,
    onError,
  });
  const declines = useMutation({
    mutationFn: ({ view }: { readonly view: InboxRequestView }) => client.inputRequests.decline(view.runId, view.requestId),
    onSuccess: refresh,
    onError,
  });

  const error = answers.error ?? forms.error ?? declines.error;
  return {
    busy: answers.isPending || forms.isPending || declines.isPending,
    error: answerErrorMessage(error),
    fieldErrors: answerFieldProblems(error),
    stale: answerIsStale(error),
    submitAnswers: async (view, submitted) => {
      await answers.mutateAsync({ view, answers: submitted });
    },
    submitForm: async (view, submission) => {
      await forms.mutateAsync({ view, submission });
    },
    decline: async (view) => {
      await declines.mutateAsync({ view });
    },
  };
}

function notificationApi(): (NotificationApiLike & (new (title: string, options?: { readonly body?: string; readonly tag?: string }) => unknown)) | null {
  const api = (globalThis as { Notification?: unknown }).Notification;
  return typeof api === "function" || (typeof api === "object" && api !== null)
    ? (api as NotificationApiLike & (new (title: string, options?: { readonly body?: string; readonly tag?: string }) => unknown))
    : null;
}

export interface NotificationControl {
  readonly status: NotificationPermissionStatus;
  readonly busy: boolean;
  /** Requests permission. Only ever called from an explicit user action. */
  readonly enable: () => Promise<NotificationPermissionStatus>;
}

export function useNotificationPermission(): NotificationControl {
  const [status, setStatus] = useState<NotificationPermissionStatus>(() => notificationPermissionStatus(notificationApi()));
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    setStatus(notificationPermissionStatus(notificationApi()));
    return () => { mounted.current = false; };
  }, []);
  const enable = useCallback(async () => {
    const api = notificationApi();
    if (api === null || typeof api.requestPermission !== "function") return "unsupported" as const;
    setBusy(true);
    try {
      await api.requestPermission();
      const next = notificationPermissionStatus(api);
      if (mounted.current) setStatus(next);
      return next;
    } catch {
      const next = notificationPermissionStatus(api);
      if (mounted.current) setStatus(next);
      return next;
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, []);
  return { status, busy, enable };
}

/**
 * Raise one OS-level notification for a newly listed question.
 *
 * Deliberately generic: the notification can be read on a lockscreen, so it says
 * that Dayvise needs input and nothing about the question. The authenticated app
 * is where the details are read. Returns whether the platform accepted it.
 */
export function raiseDesktopNotification(): boolean {
  const api = notificationApi();
  if (api === null || api.permission !== "granted") return false;
  try {
    new api("Dayvise needs your input", { body: "Open Dayvise to answer the agent's question." });
    return true;
  } catch {
    return false;
  }
}

export interface InputRequestInboxOptions {
  /** Opens the conversation holding the request, where the full transcript lives. */
  readonly onOpenConversation?: ((conversationId: string) => void) | undefined;
  /** Whether this client raises its own OS notification for a new question. */
  readonly desktopNotifications?: boolean;
}

export interface InputRequestInboxControls {
  readonly views: readonly InboxRequestView[];
  readonly loading: boolean;
  readonly refreshing: boolean;
  readonly error: string | null;
  readonly refresh: () => void;
  readonly selectedId: string | null;
  readonly selected: InboxRequestView | null;
  readonly select: (requestId: string | null) => void;
  /** The request the popup shows, or `null` when none (or all dismissed) remain. */
  readonly popup: InboxRequestView | null;
  readonly dismissPopup: (requestId: string) => void;
  readonly answers: InputRequestAnswerControls;
  readonly notifications: NotificationControl;
  readonly deliveryLimitNote: string;
}

export function useInputRequestInbox(options: InputRequestInboxOptions = {}): InputRequestInboxControls {
  const query = useInputRequests();
  const answers = useInputRequestAnswers();
  const notifications = useNotificationPermission();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<readonly string[]>([]);
  const raised = useRef<readonly string[]>([]);

  const now = useMemo(() => {
    const parsed = query.data?.now === undefined ? Number.NaN : Date.parse(query.data.now);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }, [query.data?.now]);
  const views = useMemo(() => buildInboxViews(query.data?.requests ?? [], now), [query.data, now]);
  const selected = views.find((view) => view.requestId === selectedId && view.answerable) ?? null;
  const popup = nextPopupRequest(views, dismissed);

  const desktopNotifications = options.desktopNotifications === true;

  // A question that arrives while the client is open is announced once, and only
  // for a request this client has not already announced: a poll must not ring twice
  // for the same question.
  useEffect(() => {
    if (!desktopNotifications || notifications.status !== "granted" || !pageIsVisible()) return;
    const fresh = views.filter((view) => view.answerable && !raised.current.includes(view.requestId));
    if (fresh.length === 0) return;
    raised.current = [...raised.current, ...fresh.map((view) => view.requestId)].slice(-50);
    raiseDesktopNotification();
  }, [desktopNotifications, notifications.status, views]);

  // A request that settled elsewhere must not leave its form open on this device.
  useEffect(() => {
    if (selectedId !== null && !views.some((view) => view.requestId === selectedId && view.answerable)) setSelectedId(null);
  }, [selectedId, views]);

  return {
    views,
    loading: query.isPending,
    refreshing: query.isFetching,
    error: query.error === null || query.error === undefined ? null : (query.error instanceof Error ? query.error.message : "The pending questions could not be read."),
    refresh: () => { void query.refetch(); },
    selectedId,
    selected,
    select: setSelectedId,
    popup,
    dismissPopup: (requestId) => setDismissed((current) => (current.includes(requestId) ? current : [...current, requestId])),
    answers,
    notifications,
    deliveryLimitNote: INBOX_DELIVERY_LIMIT,
  };
}
